import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { useHandler } from "@redotech/react-util/hook";
import {
  useCloseSupportTicketAction,
  useReopenSupportTicketAction,
  type SupportTicketAction,
} from "../actions/use-support-ticket-actions";

export interface UseSupportTicketActionToolbarResult {
  /** The primary close/reopen action based on current conversation status */
  primaryAction: SupportTicketAction;
  /** The conversation context for isEnabled/isVisible checks */
  conversation: SupportTicketListItemResponse;
  /** Execute the primary action */
  handlePrimaryAction: () => void;
}

/**
 * Hook that provides the primary conversation action for a toolbar.
 * The full action menu is handled by SupportTicketActionsDropdown.
 */
export function useSupportTicketActionToolbar(
  supportTicketId: SupportTicketId,
  conversation: SupportTicketListItemResponse,
): UseSupportTicketActionToolbarResult {
  const closeAction = useCloseSupportTicketAction();
  const reopenAction = useReopenSupportTicketAction();

  const isClosed = conversation.status === "CLOSED";
  const primaryAction = isClosed ? reopenAction : closeAction;

  const handlePrimaryAction = useHandler(() => {
    const isEnabled = primaryAction.isEnabled?.(conversation) ?? true;
    if (isEnabled) {
      void primaryAction.execute(supportTicketId);
    }
  });

  return { primaryAction, conversation, handlePrimaryAction };
}
